"use client";

import { create } from "zustand";

/** `recovery` é o "esqueci minha senha" — mesmo modal, terceira view. */
type AuthView = "login" | "register" | "recovery";

/** Etapa do login: identificador (1) → senha (2). Mora aqui para o header do
 *  modal poder mostrar a seta de voltar quando estiver na senha. */
export type LoginStep = "identifier" | "password";

/**
 * Dados levados para o cadastro já preenchido. Duas origens hoje: o login
 * social (nome e e-mail do provedor) e o pré-cadastro importado pela loja, que
 * além de preencher TRAVA documento e e-mail — eles vieram da planilha e o
 * `POST /customer/register` exige que cheguem iguais.
 */
export interface RegisterPrefill {
    firstName?: string;
    lastName?: string;
    email?: string;
    cpfCnpj?: string;
    /**
     * Veio de um pré-cadastro. Só o identificador usado no login entra (e trava);
     * o outro fica em branco de propósito — a pessoa precisa digitá-lo, e ele tem
     * de bater com o cadastro da loja para o registro ser aceito.
     */
    isPreRegistration?: boolean;
}

/**
 * O que o cliente já havia digitado no login, levado para a recuperação de
 * senha. Tanto faz ser CPF, e-mail ou telefone: quem sai do login para o
 * "esqueci minha senha" não deve digitar o mesmo dado duas vezes.
 */
export interface RecoveryPrefill {
    identifier?: string;
    cpfCnpj?: string;
    email?: string;
    phone?: string;
}

interface AuthModalState {
    open: boolean;
    view: AuthView;
    loginStep: LoginStep;
    registerPrefill: RegisterPrefill | null;
    recoveryPrefill: RecoveryPrefill | null;
    openModal: (view?: AuthView) => void;
    close: () => void;
    setView: (view: AuthView) => void;
    setLoginStep: (step: LoginStep) => void;
    /** Abre o cadastro já preenchido (ex.: conta social sem cadastro). */
    openRegisterPrefilled: (prefill: RegisterPrefill) => void;
    /** Vai para a recuperação de senha levando o que já foi digitado no login. */
    openRecovery: (prefill?: RecoveryPrefill) => void;
}

/**
 * Estado do modal de auth — global de propósito: é UI client-only
 * (nunca lido no SSR), então não há risco de vazar entre requests.
 * Qualquer componente abre com useAuthModal((s) => s.openModal).
 */
export const useAuthModal = create<AuthModalState>()((set) => ({
    open: false,
    view: "login",
    loginStep: "identifier",
    registerPrefill: null,
    recoveryPrefill: null,
    // Sempre reinicia o login no step do identificador ao abrir/trocar/fechar.
    openModal: (view = "login") =>
        set({ open: true, view, loginStep: "identifier", registerPrefill: null, recoveryPrefill: null }),
    close: () =>
        set({ open: false, loginStep: "identifier", registerPrefill: null, recoveryPrefill: null }),
    setView: (view) => set({ view, loginStep: "identifier" }),
    setLoginStep: (loginStep) => set({ loginStep }),
    openRegisterPrefilled: (prefill) =>
        set({ open: true, view: "register", loginStep: "identifier", registerPrefill: prefill }),
    openRecovery: (prefill) =>
        set({ open: true, view: "recovery", loginStep: "identifier", recoveryPrefill: prefill ?? null }),
}));
